/**
 * Server functions for CIH CCOs (Coach Certifying Organizations).
 *
 * CCOs are organization content pages managed by admins.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: ccos, cih ccos, organization crud, cco lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { CCOType } from "~/lib/schemas/db-enums";
import {
  createCcoInputSchema,
  updateCcoInputSchema,
  type CihCcoRow,
  type CihCcoListRow,
  type CcoWithRelations,
  type CcoArticleRow,
} from "~/lib/server/cih/ccos";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type {
  CihCcoRow,
  CihCcoListRow,
  CcoWithRelations,
  CcoArticleRow,
} from "~/lib/server/cih/ccos";

// ============================================================================
// Query Keys
// ============================================================================

export const ccoQueryKeys = {
  all: ["cih-ccos"] as const,
  list: (status?: string, type?: string) =>
    ["cih-ccos", "list", status ?? "all", type ?? "all"] as const,
  detail: (id: string) => ["cih-ccos", id] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * List all CCOs (admin) with coach + CTP counts. Excludes draft-of rows by default.
 * Optionally filter by status and/or type (pao/cto).
 */
export const getCihCcosFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(
    z
      .object({
        status: z.string().optional(),
        type: z.nativeEnum(CCOType).optional(),
      })
      .optional(),
  )
  .handler(async ({ data }): Promise<CihCcoListRow[]> => {
    const { listCcos } = await import("~/lib/server/cih/ccos");
    return listCcos(data);
  });

/**
 * Get a single CCO by ID (admin).
 */
export const getCihCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<CihCcoRow | null> => {
    const { getCco } = await import("~/lib/server/cih/ccos");
    return getCco(data);
  });

/**
 * Update CCO fields (admin, partial update).
 */
export const updateCihCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updateCcoInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateCco } = await import("~/lib/server/cih/ccos");
    return updateCco(data);
  });

/**
 * Admin self-publish a draft CCO (skips review).
 */
export const publishCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { publishCco } = await import("~/lib/server/cih/ccos");
    return publishCco(data, { userId: context.user.id });
  });

/**
 * Archive a published CCO.
 */
export const archiveCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { archiveCco } = await import("~/lib/server/cih/ccos");
    return archiveCco(data, { userId: context.user.id });
  });

/**
 * Create an editable draft of a published CCO.
 */
export const createCcoDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<CihCcoRow> => {
    const { createDraftOfPublishedCco } = await import("~/lib/server/cih/ccos");
    return createDraftOfPublishedCco(data, { userId: context.user.id });
  });

/**
 * Promote a draft back onto its published parent, recorded in the page
 * change history.
 */
export const promoteCcoDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ publishedId: string }> => {
    const { publishDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    return publishDraftAsStaff({
      kind: "organization",
      id: data.draftId,
      actorUserId: context.user.id,
      via: "promote",
    });
  });

/**
 * Restore an archived CCO to draft.
 */
export const restoreCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { restoreCco } = await import("~/lib/server/cih/ccos");
    return restoreCco(data, { userId: context.user.id });
  });

/**
 * Approve an in_review CCO (publish it).
 */
export const approveCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ publishedId: string }> => {
    const { publishDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    return publishDraftAsStaff({
      kind: "organization",
      id: data.id,
      actorUserId: context.user.id,
      via: "approve",
    });
  });

/**
 * Return an in_review CCO to draft with feedback.
 */
export const returnCcoToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { returnDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    await returnDraftAsStaff({
      kind: "organization",
      id: data.id,
      actorUserId: context.user.id,
      note: data.note,
    });
    return { success: true };
  });

/**
 * Hard-delete a draft CCO. Only allowed when status='draft' AND draftOf IS NULL
 * (never-published drafts — typos, test data, wrong type). Archived / published
 * content must go through the lifecycle (archive / restore) instead.
 * Fails with a friendly message if FK constraints block the delete.
 */
export const deleteCihCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { deleteCco } = await import("~/lib/server/cih/ccos");
    return deleteCco(data);
  });

/**
 * Create a new CCO as draft with minimal fields.
 * Returns the created row so the caller can navigate to the editor.
 */
export const createCihCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createCcoInputSchema)
  .handler(async ({ data, context }): Promise<CihCcoRow> => {
    const { createCco } = await import("~/lib/server/cih/ccos");
    return createCco(data, { userId: context.user.id });
  });

/**
 * Get a published CCO by slug with related coaches, CTPs, and frameworks.
 * Public — no auth required.
 */
export const getPublishedCcoWithRelationsFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<CcoWithRelations | null> => {
    const { getPublishedCcoWithRelations } =
      await import("~/lib/server/cih/ccos");
    return getPublishedCcoWithRelations(data);
  });

/**
 * Whether a slug that failed to resolve belongs to an archived organization,
 * so the route can answer `410 Gone` instead of `404`. Public — it exposes one
 * bit about a URL the crawler already has. Only called after a miss.
 */
export const isCcoArchivedFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<boolean> => {
    const { isCcoArchived } = await import("~/lib/server/cih/ccos");
    return isCcoArchived(data);
  });

/**
 * Get the full deduplicated coach list for a published CCO by slug.
 * Combines direct CCO links (coachesCcos) and CTP-mediated links (coachesCtps → ctps.ccoId).
 * Public — no auth required.
 */
export const getAllCcoCoachesFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<CcoWithRelations["coaches"]> => {
    const { getAllCcoCoaches } = await import("~/lib/server/cih/ccos");
    return getAllCcoCoaches(data);
  });

/**
 * Get the full list of published CTPs under a published CCO by slug.
 * Public — no auth required.
 */
export const getAllCcoCtpsFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(
    async ({
      data,
    }): Promise<{
      cco: CihCcoRow | null;
      ctps: CcoWithRelations["ctps"];
    }> => {
      const { getAllCcoCtps } = await import("~/lib/server/cih/ccos");
      return getAllCcoCtps(data);
    },
  );

/**
 * Get challenges reachable from a CCO via its frameworks or CTPs.
 * Path A: frameworks.ccoId → challengeFrameworks → challenges
 * Path B: ctps.ccoId → ctpFrameworks → frameworks → challengeFrameworks → challenges
 * Union + dedupe, published only.
 */
export const getChallengesByCcoFn = createServerFn({ method: "GET" })
  .validator(z.object({ ccoId: z.string().uuid() }))
  .handler(
    async ({
      data,
    }): Promise<{ id: string; title: string; slug: string | null }[]> => {
      const { getChallengesByCco } = await import("~/lib/server/cih/ccos");
      return getChallengesByCco(data);
    },
  );

/**
 * Get recent published articles by coaches affiliated with this CCO.
 * Coach affiliation path: coachesCcos OR (coachesCtps → ctps.ccoId = this).
 */
export const getCcoArticlesFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      ccoId: z.string().uuid(),
      limit: z.number().int().positive().optional(),
    }),
  )
  .handler(async ({ data }): Promise<CcoArticleRow[]> => {
    const { getCcoArticles } = await import("~/lib/server/cih/ccos");
    return getCcoArticles(data);
  });

/**
 * Count coaches who are members of a CCO. Returns a plain count for summary display.
 */
export const getCcoCoachMembershipCountFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ccoId: z.string().uuid() }))
  .handler(async ({ data }): Promise<number> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCcos } = await import("~/../drizzle/schema");
    const { count, eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const [result] = await db
      .select({ total: count() })
      .from(coachesCcos)
      .where(eq(coachesCcos.ccoId, data.ccoId));
    return result?.total ?? 0;
  });

// ============================================================================
// Query Options
// ============================================================================

export const cihCcosQueryOptions = (status?: string, type?: CCOType) =>
  queryOptions({
    queryKey: ccoQueryKeys.list(status, type),
    queryFn: () => getCihCcosFn({ data: { status, type } }),
  });

export const cihCcoQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ccoQueryKeys.detail(id),
    queryFn: () => getCihCcoFn({ data: { id } }),
  });
